<template>
	<Accordion
		:title="labels.title"
		icon="preview-outline"
		:icon-hint="transUcfirst('tag_group.visible_in_player')"
		:open="open"
		:disabled="disabled"
		:disabled-hint="disabledHint"
		@toggle="$emit('toggle', $event)"
	>
		<MobileAthleteSelector
			v-if="showAthleteSelector && isMobileWebApp"
			:athletes="athletes"
			:value="selectedAthleteComp"
			@input="setSelected"
		/>
		<AthleteSelector
			v-if="showAthleteSelector && !isMobileWebApp"
			:athletes="athletes"
			:value="selectedAthleteComp"
			@input="setSelected"
		/>

		<div class="clip-name">
			<FormInput
				v-model="clipName"
				:disabled="disabled"
				:disabled-hint="disabledHint"
				:maxlength="MAX_LENGTH"
			>
				<template #trailing>
					<Icon
						icon-name="pen"
						size="md"
					/>
				</template>
			</FormInput>
			<div class="chars-counter">
				{{ nameLength }}/{{ MAX_LENGTH }}
			</div>
		</div>
		<div class="buttons">
			<TagButton
				:enabled="markInEnabled"
				:highlighted="markInEnabled"
				:active="markOutEnabled"
				:label="markInLabel"
				class="marking-button"
				icon-name="pointing-left"
				icon-size="lg"
				:is-full-width="true"
				:label-shadow="false"
				@click="onMarkInClick()"
			/>
			<TagButton
				:enabled="markOutEnabled"
				:highlighted="markOutEnabled"
				:active="false"
				:label="markOutLabel"
				class="marking-button  mark-out-button"
				icon-name="pointing-right"
				icon-size="lg"
				icon-position="after"
				:label-shadow="false"
				@click="onMarkOutClick()"
			>
				<template #label>
					<div>{{ labels.markOut }}</div>
					<div
						v-if="isMarkingAthlete"
						class="marking-duration"
					>
						{{ durationFormatted }}
					</div>
				</template>
			</TagButton>
		</div>
		<div
			v-if="isMarkingAthlete"
			class="under"
		>
			<div
				class="button cancel"
				@click="onCancel"
			>
				{{ labels.cancel }}
			</div>
			<div
				v-if="warning"
				class="warning"
			>
				<Icon
					icon-name="c-info"
					size="md"
				/>
				{{ warning }}
			</div>
		</div>
	</Accordion>
</template>
<script>
import MobileAthleteSelector from '@videotools/js/one/components/AthleteSelector/MobileAthleteSelector';
import TagButton from '@videotools/js/one/components/TagButton/TagButton';
import AthleteSelector from '@videotools/js/one/components/AthleteSelector/AthleteSelector';
import Accordion from '@components/accordion/Accordion';
import SectionLabelsMixin from '@videotools/js/one/components/TagGroups/mixin/SectionLabelsMixin';
import { mapActions, mapGetters } from 'vuex';
import { SECTION_MARKING_ATHLETE } from '@videotools/js/api/entities/TagGroup';
import moment from 'moment';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import { ATHLETE_UNKNOWN_KEY } from '@videotools/js/constants/athletes';
import { bus, EVENT } from '@videotools/js/events';
import FormInput from '@videotools/js/one/components/Form/FormInput';
import transHelpers from '@videotools/js/util/transHelpers';
import { TAG_NAME_CUT } from '@videotools/js/api/entities/TagButton';

const events = [EVENT.PLAYER.ON.PROGRESS];
const DEFAULT_CLIP_NAME = `${transHelpers.transTagLabel(TAG_NAME_CUT)}`;
const MAX_LENGTH = 100;

export default {
	name:       'AthleteMarkingSection',
	components: { FormInput, TagButton, MobileAthleteSelector, AthleteSelector, Accordion },
	mixins:     [SectionLabelsMixin],
	props:      {
		markInClass: {
			type: String,
		},
		markOutClass: {
			type: String,
		},
		open: {
			type:    Boolean,
			default: false,
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		disabledHint: {
			type:    String,
			default: null,
		},
		selectedAthlete: {
			type: Object,
		},
		showAthleteSelector: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			elapsed:   0,
			startTime: 0,
			hasError:  false,
			clipName:  DEFAULT_CLIP_NAME,
			MAX_LENGTH,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			isAppleDevice:     'device/isAppleDevice',
			event:             'event/event',
			athletes:          'event/participants',
			markingActive:     'markingZone/active',
			markingZoneStart:  'markingZone/start',
			isMarkingAthlete:  'markingZone/isMarkingAthlete',
			athleteMarkingTag: 'tags/athleteMarkingTag',
		}),
		labels() {
			return {
				markIn:  this.$I18n.trans('one.start'),
				markOut: this.$I18n.trans('one.finish'),
				title:   this.translateSectionLabel(SECTION_MARKING_ATHLETE, true),
				cancel:  this.transUcfirst('commons.cancel'),
			};
		},
		duration() {
			const duration = this.elapsed - this.startTime;
			return duration > 0 ? duration : 0;
		},
		markInEnabled() {
			return !this.hasError && !this.markingActive;
		},
		markOutEnabled() {
			return !this.hasError && this.markingActive && !this.warning && this.isMarkingAthlete;
		},
		elapsedFormatted() {
			return moment.utc(moment.duration(this.elapsed - this.startTime, 's').asMilliseconds()).format('mm:ss');
		},
		durationFormatted() {
			return formatTimestampAsMinutesSeconds(this.duration);
		},
		markInLabel() {
			return this.labels.markIn;
		},
		markOutLabel() {
			if (this.markOutEnabled) {
				return this.isMobileWebApp
					? `${this.labels.markOut} ${this.elapsedFormatted} `
					: `${this.elapsedFormatted} ${this.labels.markOut} `;
			}

			return this.labels.markOut;
		},
		selectedAthleteNormalized() {
			return this.selectedAthlete?.id === ATHLETE_UNKNOWN_KEY ? null : this.selectedAthlete;
		},
		warning() {
			if (!this.isMarkingAthlete) {
				return null;
			}

			if (this.markingActive && this.markingZoneStart > this.elapsed) {
				return this.transUcfirst('one.run_position_invalid');
			}

			return null;
		},
		selectedAthleteComp: {
			get() {
				return this.selectedAthlete;
			},
			set() {},
		},
		nameLength() {
			return this.clipName.length;
		},
	},
	mounted() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
		if (!this.athleteMarkingTag) {
			this.hasError = true;
			throw new Error('Run tag type is missing');
		}
	},
	destroyed() {
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			startMarking:   'markingZone/startMarking',
			endMarking:     'markingZone/endMarking',
			cancelMarking:  'markingZone/cancel',
			createEventTag: 'event/createEventTag',
		}),
		setSelected(value) {
			const normalizedAthlete = value?.id === ATHLETE_UNKNOWN_KEY ? null : value;
			this.$emit('select-athlete', normalizedAthlete);
		},
		onMarkInClick() {
			this.startTime = this.elapsed;
			this.startMarking({ position: this.elapsed, tag: this.athleteMarkingTag });
		},
		onMarkOutClick() {
			this.endMarking(this.elapsed);
			this.createCut(this.athleteMarkingTag, this.startTime, this.duration, this.selectedAthleteNormalized?.id || null);
			this.startTime = -1;
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](elapsedSeconds) {
			this.elapsed = elapsedSeconds;
		},
		onCancel() {
			this.cancelMarking();
		},
		async createCut(tag, timestamp, duration, participantId = null) {
			await this.createEventTag({
				data: {
					eventId:         this.event.id,
					tagId:           tag.tag.id,
					participantId,
					timestamp:       Math.round(timestamp),
					secondsBefore:   0,
					secondsAfter:    Math.round(duration),
					timeline:        tag.timeline,
					taggingToolOnly: tag.taggingToolOnly,
					priority:        tag.priority,
					highlight:       tag.highlight,
					name:            this.clipName || DEFAULT_CLIP_NAME,
				},
				backendSnap: this.isAppleDevice,
				absolute:    true,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import "@videotools/sass/_fonts.scss";

.buttons {
	margin-top: 24px;
	display: flex;
	justify-content: space-between;

	.marking-button {
		padding: 16px 20px;

		&.active {
			outline-color: var(--color-red);
		}

		&.highlighted {
			background: var(--color-hype-yellow);
			color: var(--color-black);
			&:hover {
				background: var(--color-hype-yellow);
			}
		}

		.is-mobile & {
			padding: 16px 20px;
			max-width: 163px;
			height: 80px;
		}
	}

	.mark-out-button {
		padding-top: 0;
		padding-bottom: 0;
		min-height: 56px;

		&.highlighted {
			background: var(--color-red);
			color: var(--color-white);
			&:hover {
				background: var(--color-red);
			}
		}
	}

	.marking-duration {
		@include text-sm-2;
	}
}

.under {
	margin-top: 16px;
	display: flex;

	.warning {
		margin-left: 16px;
		display: flex;
		align-items: center;
		@include text-sm-2;
		color: var(--color-grey-300);

		.v2-icon {
			margin-right: 8px;
		}
	}
}

.button.cancel {
	@include text-sm-2;
	color: var(--color-soft-white);
	background: none;
	border-color: var(--color-soft-white);
	width: 78px;
	height: 32px;
	padding: 0;
	min-width: 78px;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
}

.clip-name {
	margin-bottom: 16px;
}

.athlete-selector {
	margin-bottom: 16px;
}

.chars-counter {
  margin-top: 6px;
  width: 100%;
  text-align: right;
  color: var(--color-grey-300);
  @include text-sm-2;
}
</style>
